/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Subscription, BehaviorSubject } from "rxjs";
import { Logger } from "../../../../core/libs/logger";

export enum WaitResultCode {
   WAIT_RC_ERROR = -2,
   WAIT_RC_TIMEOUT = -1,
   WAIT_RC_OK = 0
}
export enum EventAction {
   DEFAULT = 0,
   SET = 1,
   RESET = 2,
   UNSET = 3,
   DESTROY = 4
}
export const INFINITE = -1;

class PendingPromise {
   private fired = false;
   private subscription: Subscription;
   private pendingPromise: Promise<WaitResultCode>;
   private timeoutTimer: number;
   private resolvePromise: (WaitResultCode) => void;

   private _resolve = (result: WaitResultCode) => {
      if (this.fired) {
         return false;
      }
      this.fired = true;
      if (this.timeoutTimer) {
         clearTimeout(this.timeoutTimer);
      }
      this.timeoutTimer = null;
      this.resolvePromise(result);
      if (this.subscription) {
         this.subscription.unsubscribe();
         this.subscription = null;
      }
      if (this.pendingPromise) {
         this.pendingPromise = null;
         this.resolvePromise = null;
      }
      return true;
   };
   // need to fire with last value if exist to avoid race condition under this limited use case
   public start = (
      event: BehaviorSubject<EventAction>,
      msTimeout: number,
      debugId: string
   ): Promise<WaitResultCode> => {
      this.fired = false;
      Logger.trace("register wait as " + debugId, Logger.RDPVCBRIDGE);
      this.pendingPromise = new Promise((resolve, reject) => {
         this.resolvePromise = resolve;
         if (msTimeout !== INFINITE) {
            this.timeoutTimer = window.setTimeout(() => {
               this._resolve(WaitResultCode.WAIT_RC_TIMEOUT);
               Logger.trace("timeout reached for wait " + debugId, Logger.RDPVCBRIDGE);
            }, msTimeout);
         }
         this.subscription = event.subscribe((action: EventAction) => {
            if (action === EventAction.SET) {
               this._resolve(WaitResultCode.WAIT_RC_OK);
               Logger.trace("completed for wait " + debugId, Logger.RDPVCBRIDGE);
            } else if (action === EventAction.DESTROY) {
               this._resolve(WaitResultCode.WAIT_RC_ERROR);
               Logger.trace("error for wait " + debugId, Logger.RDPVCBRIDGE);
            }
         });
      });
      this.pendingPromise["debugId"] = debugId;
      return this.pendingPromise;
   };
}
/**
 * simplified version of VMEvent
 * don't support reset
 * support multiple wait invocation.
 * thus instead of using Subject<EventAction>
 */
export class AsyncWaitPromise {
   private name: string;
   private debugCounter: number = 1;
   private debugID: string;
   private stateCore$: BehaviorSubject<EventAction>;
   constructor() {
      this.stateCore$ = new BehaviorSubject<EventAction>(EventAction.DEFAULT);
      this.name = null;
   }
   create = (name: string = "") => {
      this.destroy();
      this.name = name;
      this.generateDebugID();
   };
   destroy = (): boolean => {
      this.stateCore$.next(EventAction.DESTROY);
      this.stateCore$.next(EventAction.DEFAULT);
      this.name = null;
      return true;
   };
   isValid = (): boolean => {
      return this.name !== null;
   };
   isntValid = (): boolean => {
      return !this.isValid();
   };

   /**
    * Sets the event to a signaled state.
    * @returns Returns TRUE if the OS didn't return an error.
    */
   set = (): boolean => {
      Logger.debug(this.name + " event fired", Logger.RDPVCBRIDGE);
      if (this.isValid()) {
         this.stateCore$.next(EventAction.SET);
      }
      return true;
   };
   /**
    *
    * @param msTimeout
    */
   wait = (msTimeout: number = INFINITE): Promise<WaitResultCode> => {
      const promise = new PendingPromise();
      return promise.start(this.stateCore$, msTimeout, this.debugID);
   };

   public generateDebugID = () => {
      this.debugID = this.debugCounter++ + " for " + this.name;
   };

   public getDebugID = (): string => {
      return this.debugID;
   };
}
